import React, { useState, useMemo, useRef } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { DragDropContext, Droppable, Draggable, DropResult } from '@hello-pangea/dnd';
import { CustomSelect, StatusDot, ListSearchInput, btnPrimary } from '@redpanda/ui';
import { formatTableDate } from '@redpanda/utils';
import { useTaskStore } from '../stores/pmTaskStore';
import { refetchProjectItems } from '../stores/createProjectItemStore';
import { updateTask } from '../services/api';
import { toast } from 'react-hot-toast';
import TaskDialog from '../components/TaskDialog';
import ProjectHeader from '../components/ProjectHeader';
import { useProject } from '../hooks/useProject';
import { notifyDataChanged, useDataChanged } from '../utils/dataEvents';
import { priorityTone, taskTone, toneOf } from '../utils/statusTone';
import { isPastDue } from '../utils/dateUtils';
import { pendingRowClass } from '../utils/pendingItem';
import { PMTask } from '../types/pm';
import { useMembers, memberName } from '../stores/lookupStore';
import { checklistProgress } from '../components/TaskChecklist';

const COLUMNS = ['PENDING', 'IN_PROGRESS', 'BLOCKED', 'COMPLETED'] as const;
type ColumnStatus = typeof COLUMNS[number];

const KanbanPage: React.FC = () => {
  const { t, i18n } = useTranslation(['translation', 'pages']);
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const returnTab = new URLSearchParams(location.search).get('returnTab') || undefined;

  const { project, loading: projectLoading } = useProject(id);
  const { items: allTasks, update: updateTaskInStore } = useTaskStore();

  const [searchQuery, setSearchQuery] = useState('');
  const [filterPriority, setFilterPriority] = useState('');
  const [filterAssignee, setFilterAssignee] = useState('');
  const [showTaskDialog, setShowTaskDialog] = useState(false);
  const [editingTask, setEditingTask] = useState<PMTask | null>(null);
  // 拖曳換狀態時畫面已更新，通知其他頁時略過自己的重抓
  const skipOwnRefresh = useRef(false);

  const projectTasks = useMemo(() => allTasks.filter(task => task.projectId === id), [allTasks, id]);

  // 進頁面時任務由 useProject 一起抓回（GET /projects/:id 帶子資料）；之後別頁改了任務再單獨重抓
  useDataChanged('tasks', () => {
    if (skipOwnRefresh.current) return;
    if (id) refetchProjectItems(useTaskStore, 'tasks', id).catch(console.error);
  });

  const search = searchQuery.trim().toLowerCase();
  const filteredTasks = useMemo(() => projectTasks.filter(task =>
    (!search || task.title.toLowerCase().includes(search)) &&
    (!filterPriority || task.priority === filterPriority) &&
    (!filterAssignee || task.assignedTo === filterAssignee)
  ), [projectTasks, search, filterPriority, filterAssignee]);

  const tasksByStatus = useMemo(() => {
    const groups: Record<ColumnStatus, PMTask[]> = { PENDING: [], IN_PROGRESS: [], BLOCKED: [], COMPLETED: [] };
    filteredTasks.forEach(task => {
      // 舊資料的 TODO／REVIEW 併入待開始／進行中
      const status = task.status === 'TODO' ? 'PENDING' : task.status === 'REVIEW' ? 'IN_PROGRESS' : task.status;
      if (status in groups) groups[status as ColumnStatus].push(task);
    });
    return groups;
  }, [filteredTasks]);

  const members = useMembers();
  const assignees = useMemo(
    () => Array.from(new Set(projectTasks.map(task => task.assignedTo).filter(Boolean) as string[])),
    [projectTasks]
  );

  const hasFilters = !!(searchQuery || filterPriority || filterAssignee);
  const clearFilters = () => {
    setSearchQuery('');
    setFilterPriority('');
    setFilterAssignee('');
  };

  const handleDragEnd = async (result: DropResult) => {
    const { source, destination, draggableId } = result;
    if (!destination || source.droppableId === destination.droppableId) return;

    const task = projectTasks.find(item => item.id === draggableId);
    if (!task) return;
    const newStatus = destination.droppableId;

    updateTaskInStore(task.id, { status: newStatus });
    try {
      await updateTask(task.id, { status: newStatus });
      skipOwnRefresh.current = true;
      notifyDataChanged('tasks');
    } catch (error) {
      console.error('Failed to update task status:', error);
      updateTaskInStore(task.id, { status: task.status });
      toast.error(t('pmTasks.statusUpdateError', { ns: 'pages' }));
    } finally {
      skipOwnRefresh.current = false;
    }
  };

  const openTask = (task: PMTask | null) => {
    setEditingTask(task);
    setShowTaskDialog(true);
  };

  if (!id) return null;

  return (
    <div className="container mx-auto px-10 py-6 pb-32">
      <ProjectHeader
        projectId={id}
        project={project}
        current="kanban"
        returnTab={returnTab}
        action={
          <button type="button" onClick={() => openTask(null)} className={btnPrimary}>
            {t('pmTasks.addTask', { ns: 'pages' })}
          </button>
        }
      />

      {/* 篩選列（R18）：搜尋＋下拉固定寬；有條件時才出現「清除篩選」 */}
      <div className="mb-4 flex flex-col md:flex-row md:items-center gap-3">
        <ListSearchInput value={searchQuery} onChange={setSearchQuery} placeholder={t('pmKanban.searchPlaceholder', { ns: 'pages' })} />
        <div className="md:w-48">
          <CustomSelect
            value={filterPriority}
            onChange={setFilterPriority}
            placeholder={t('pmTasks.fields.priority', { ns: 'pages' })}
            options={[
              { value: '', label: t('pmKanban.allPriorities', { ns: 'pages' }) },
              ...(['HIGH', 'MEDIUM', 'LOW'] as const).map(p => ({ value: p, label: t(`pmTasks.priority.${p}`, { ns: 'pages' }) })),
            ]}
          />
        </div>
        {assignees.length > 0 && (
          <div className="md:w-48">
            <CustomSelect
              value={filterAssignee}
              onChange={setFilterAssignee}
              placeholder={t('pmTasks.fields.assignedTo', { ns: 'pages' })}
              options={[
                { value: '', label: t('pmKanban.allAssignees', { ns: 'pages' }) },
                ...assignees.map(assignee => ({ value: assignee, label: memberName(members, assignee) })),
              ]}
            />
          </div>
        )}
        {hasFilters && (
          <button type="button" onClick={clearFilters} className="text-sm font-medium text-fg-secondary hover:text-fg">
            {t('common.filters.clear')}
          </button>
        )}
      </div>

      {/* 看板（§3-10）：欄寬固定、橫向捲動；欄頭＝狀態點＋欄名＋數量，不用飽和色實心欄頭 */}
      {projectLoading && projectTasks.length === 0 ? (
        <div className="flex gap-4 overflow-hidden">
          {COLUMNS.map(status => (
            <div key={status} className="w-72 shrink-0 h-64 rounded-lg border border-line bg-surface animate-pulse motion-reduce:animate-none" />
          ))}
        </div>
      ) : (
        <DragDropContext onDragEnd={handleDragEnd}>
          <div className="flex gap-4 overflow-x-auto pb-2">
            {COLUMNS.map(status => (
              <section key={status} className="w-72 shrink-0 flex flex-col rounded-lg bg-slate-100/70 dark:bg-slate-900/60 border border-line">
                <header className="flex items-center gap-2 px-3 py-2.5">
                  <StatusDot tone={toneOf(taskTone, status)} label={t(`pmTasks.status.${status}`, { ns: 'pages' })} />
                  <span className="text-[13px] text-fg-secondary tabular-nums">{tasksByStatus[status].length}</span>
                </header>

                <Droppable droppableId={status}>
                  {(provided, snapshot) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.droppableProps}
                      className={`flex-1 min-h-[24rem] px-2 pb-2 space-y-2 rounded-b-lg transition-colors ${
                        snapshot.isDraggingOver ? 'bg-accent-soft' : ''
                      }`}
                    >
                      {tasksByStatus[status].length === 0 && !snapshot.isDraggingOver && (
                        <p className="pt-6 text-center text-[13px] text-fg-muted">{t('pmKanban.dropHere', { ns: 'pages' })}</p>
                      )}
                      {tasksByStatus[status].map((task, index) => {
                        const overdue = task.status !== 'COMPLETED' && isPastDue(task.dueDate);
                        return (
                          <Draggable key={task.id} draggableId={task.id} index={index} isDragDisabled={task.id.startsWith('temp-')}>
                            {(dragProvided, dragSnapshot) => (
                              <div
                                ref={dragProvided.innerRef}
                                {...dragProvided.draggableProps}
                                {...dragProvided.dragHandleProps}
                                onClick={() => openTask(task)}
                                className={`bg-surface rounded-md border border-line p-3 cursor-pointer hover:border-slate-300 dark:hover:border-slate-600 transition-colors ${
                                  dragSnapshot.isDragging ? 'shadow-lg' : ''
                                } ${pendingRowClass(task)}`}
                              >
                                <h4 className="text-sm font-medium text-fg line-clamp-2">{task.title}</h4>
                                {task.assignedTo && (
                                  <p className="mt-1 text-[13px] text-fg-secondary truncate">{memberName(members, task.assignedTo)}</p>
                                )}
                                <div className="mt-2 flex items-center justify-between gap-2 text-[13px]">
                                  <span className={`tabular-nums ${overdue ? 'text-red-600 dark:text-red-400' : 'text-fg-secondary'}`}>
                                    {task.dueDate ? formatTableDate(task.dueDate, i18n.language) : ''}
                                    {overdue && <span className="ml-1">{t('pmTasks.overdue', { ns: 'pages' })}</span>}
                                  </span>
                                  <span className="flex items-center gap-3">
                                    {checklistProgress(task.checklist) && (
                                      <span className="text-fg-secondary tabular-nums" title={t('pmTasks.checklist.label', { ns: 'pages' })}>☑ {checklistProgress(task.checklist)}</span>
                                    )}
                                    <StatusDot tone={toneOf(priorityTone, task.priority)} label={t(`pmTasks.priority.${task.priority}`, { ns: 'pages' })} />
                                  </span>
                                </div>
                              </div>
                            )}
                          </Draggable>
                        );
                      })}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </section>
            ))}
          </div>
        </DragDropContext>
      )}

      {hasFilters && filteredTasks.length === 0 && projectTasks.length > 0 && (
        <p className="mt-4 text-[13px] text-fg-secondary">{t('common.filters.noResultsHint')}</p>
      )}

      {showTaskDialog && (
        <TaskDialog
          isOpen={showTaskDialog}
          projectId={id}
          task={editingTask ?? undefined}
          onClose={() => {
            setShowTaskDialog(false);
            setEditingTask(null);
          }}
        />
      )}
    </div>
  );
};

export default KanbanPage;
